import {
  type Data,
  UniqueIdentifier,
  useDraggable,
  useDroppable,
  useDndContext,
} from "@dnd-kit/core";
import classNames from "classnames";
import * as React from "react";

import { IS_MOBILE } from "@/shared/environment";

import { DragItem } from "./constants";
import styles from "./Draggable.module.css";
import dropStyles from "./Droppable.module.css";

export interface DraggableDroppableItem<T> extends Data {
  type: DragItem;
  data: T;
}

interface DraggableProps<T> {
  as?: React.ElementType;
  item: DraggableDroppableItem<T>;
  id: UniqueIdentifier;
  /* If an item is selected, it can only be used as a draggable */
  isSelected: boolean;
  /* If an item isDroppable, it can receive drop events */
  isDroppable?: boolean;
  className?: classNames.Argument;
}

/**
 * A wrapper that makes its children draggable AND droppable within a DnD context.
 */
export function DraggableDroppable<T>(
  props: React.PropsWithChildren<DraggableProps<T>>,
) {
  const { as, children, item, id, isDroppable = false, isSelected } = props;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: item,
    disabled: IS_MOBILE,
  });

  const dndContext = useDndContext();

  const {
    isOver,
    setNodeRef: setDroppableNodeRef,
    active,
    over,
  } = useDroppable({
    id,
    data: item,
  });

  const Component = as ?? "div";

  const setRefs = React.useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node);

      if (isDroppable) {
        setDroppableNodeRef(node);
      } else {
        setDroppableNodeRef(null);
      }
    },
    [setNodeRef, setDroppableNodeRef, isDroppable],
  );

  const isValidOver = isOver && active?.id !== over?.id && !isSelected;

  return (
    <Component
      className={classNames(
        props.className,
        {
          [styles.draggable]: !IS_MOBILE,
        },
        {
          [styles.ghosted]: isDragging || (isSelected && dndContext.activeNode),
        },
        {
          [dropStyles.droppable]: isValidOver,
          [dropStyles.dropZoneBorder]: isValidOver,
        },
      )}
      ref={setRefs}
      {...listeners}
      {...attributes}
    >
      {children}
    </Component>
  );
}
